import { useCallback, useMemo } from 'react';

import { getAdditionalAvailability, validateAdditionalQuantity } from '@src/common/additionals';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import {
  productAdditionalFormInput,
  productScheduleSelector,
  productSelector,
  selectedProductTariffHour,
  tariffsState,
  updateProductAdditional,
  type FullProduct,
  type TariffsPerType,
} from '@src/stores/product';
import { track } from '@src/services/analytics';
import type { Additional } from '@src/core/buyerApiClient';
import type { Translate } from '@src/i18n/useTranslate';

export type ProductAdditionalUpdate = Partial<{
  checked: boolean;
  input: string;
  quantity: number | undefined;
}>;

export type UseProductAdditionalParams = {
  widgetId: string;
  additional: Additional;
  productId?: string;
  i18n?: Translate['i18n'];
};

export type UseProductAdditionalResult = {
  checked: boolean;
  input: string;
  quantity: number | undefined;

  availableAmount: number | null;
  isAvailable: boolean;
  quantityError: string | undefined;

  product: FullProduct | undefined;
  selectedDate: string | undefined;
  selectedTariffMinutes: number | undefined;
  listOfTariffsPerType: TariffsPerType[];

  updateAdditional: (partial: ProductAdditionalUpdate) => void;
  trackToggle: (nextChecked: boolean) => void;
};

export function useProductAdditional({
  widgetId,
  additional,
  productId,
  i18n,
}: UseProductAdditionalParams): UseProductAdditionalResult {
  const dispatch = useAppDispatch();

  const { checked, input, quantity } = useAppSelector(productAdditionalFormInput(widgetId, additional, productId));
  const { listOfTariffsPerType, selectedDate } = useAppSelector(tariffsState(widgetId, productId));
  const { dates } = useAppSelector(productScheduleSelector(productId ?? ''));
  const product = useAppSelector(productSelector(productId ?? ''));
  const selectedTariffHour = useAppSelector(selectedProductTariffHour(widgetId, productId));
  const selectedTariffMinutes = selectedTariffHour?.minutes;

  const { availableAmount, isAvailable } = useMemo(() => {
    if (!product) {
      return { availableAmount: null, isAvailable: true };
    }

    return getAdditionalAvailability(
      dates[selectedDate ?? '']?.availables,
      additional,
      product,
      selectedDate,
      selectedTariffMinutes,
      listOfTariffsPerType,
    );
  }, [dates, selectedDate, additional, product, selectedTariffMinutes, listOfTariffsPerType]);

  const { quantityError } = useMemo(
    () =>
      validateAdditionalQuantity({
        quantity,
        additional,
        availableAmount,
        i18n,
      }),
    [quantity, additional, availableAmount, i18n],
  );

  const updateAdditional = useCallback(
    (partial: ProductAdditionalUpdate) => {
      dispatch(
        updateProductAdditional(widgetId, {
          additional,
          checked,
          input,
          quantity,
          ...partial,
        }),
      );
    },
    [dispatch, widgetId, additional, checked, input, quantity],
  );

  const trackToggle = useCallback(
    (nextChecked: boolean) => {
      track(nextChecked ? 'productAdderAdditionalSelected' : 'productAdderAdditionalRemoved', {
        productId: product?.id ?? '',
        productName: product?.name ?? '',
        additionalName: additional.title,
        additionalId: additional.id,
      });
    },
    [product?.id, product?.name, additional.id, additional.title],
  );

  return {
    checked,
    input,
    quantity,
    availableAmount,
    isAvailable,
    quantityError,
    product,
    selectedDate,
    selectedTariffMinutes,
    listOfTariffsPerType,
    updateAdditional,
    trackToggle,
  };
}
